import styled from 'styled-components';
import {
  AppFileEmbedDefault,
  AppFileEmbedDocument,
  AppFileEmbedIcon1,
  AppFileEmbedIcon2,
  AppFileEmbedIcon3,
  AppFileEmbedIcon4,
  AppFileEmbedIcon5,
  AppFileEmbedIcon6,
  AppFileEmbedPdf,
  AppFileEmbedPresentation,
  AppFileEmbedSpreadsheets
} from '@elfsight/icons';
import { Typography } from '@elfsight/widget-ui';
import { useSettings } from '@wu/widget-core';
import type {
  EmbedFileType,
  FileEmbedsFile,
  PdfEmbedIconType
} from '../../types';
import { getClassName as defaultGetClassName } from '../helpers';

export type IconType =
  | Exclude<EmbedFileType, 'img'>
  | PdfEmbedIconType
  | 'default'
  | 't';

interface IconProps {
  iconClassName: string;
  previewImage: FileEmbedsFile['previewImage'];
  size: { w: number; h: number };
  name?: IconType;
  className?: string;
  altText?: string;
  text?: string;
  tabIndex?: number;
}

const iconsMap = {
  audio: AppFileEmbedDefault,
  default: AppFileEmbedDefault,
  doc: AppFileEmbedDocument,
  document: AppFileEmbedDocument,
  'icon-1': AppFileEmbedIcon1,
  'icon-2': AppFileEmbedIcon2,
  'icon-3': AppFileEmbedIcon3,
  'icon-4': AppFileEmbedIcon4,
  'icon-5': AppFileEmbedIcon5,
  'icon-6': AppFileEmbedIcon6,
  pdf: AppFileEmbedPdf,
  pp: AppFileEmbedPresentation,
  presentation: AppFileEmbedPresentation,
  spreadsheets: AppFileEmbedSpreadsheets,
  t: AppFileEmbedDefault,
  xls: AppFileEmbedSpreadsheets
};

const defaultColorsMap = {
  audio: '#4A545E',
  default: '#4A545E',
  doc: '#2B569A',
  document: '#2B569A',
  'icon-1': undefined,
  'icon-2': undefined,
  'icon-3': undefined,
  'icon-4': undefined,
  'icon-5': undefined,
  'icon-6': undefined,
  pdf: '#DE2B33',
  pp: '#D24624',
  presentation: '#D24624',
  spreadsheets: '#0F7B10',
  t: '#4A545E',
  xls: '#0F7B10'
};

const getClassName = (component: string) =>
  defaultGetClassName('icon', component);

export function Icon({
  iconClassName,
  name,
  className,
  size,
  text,
  previewImage,
  altText,
  tabIndex
}: IconProps) {
  const { iconColor } = useSettings();

  const IconComponent = name && iconsMap[name];
  if (!previewImage && !IconComponent) {
    return null;
  }

  const renderIcon = () => {
    if (previewImage?.url) {
      return (
        <Image
          src={previewImage.url}
          alt={altText}
          tabIndex={tabIndex}
          _width={size.w}
          _height={size.h}
        />
      );
    }

    if (IconComponent) {
      return (
        <IconComponent
          className={iconClassName}
          width="30px"
          height="38px"
          fill={
            iconColor && iconColor !== 'transparent'
              ? iconColor
              : defaultColorsMap[name]
          }
        />
      );
    }

    return null;
  };

  return (
    <Container className={className}>
      {renderIcon()}

      {text && name === 'default' && !previewImage && (
        <Extension>{text}</Extension>
      )}
    </Container>
  );
}

const Container = styled.div.attrs({
  className: getClassName('container')
})`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const Image = styled.img.attrs<{
  _width: number;
  _height: number;
}>({
  className: getClassName('image')
})`
  display: block;
  max-width: ${({ _width }) => _width}px;
  max-height: ${({ _height }) => _height}px;
`;

const Extension = styled(Typography).attrs({
  className: getClassName('extension'),
  textStyle: {
    fontSize: 9,
    fontWeight: 700,
    textTransform: 'uppercase'
  },
  color: '#fff'
})`
  position: absolute;
`;
